import {
  formatKm,
  geometryToLines,
  nearestOnRoute,
  ON_COURSE_MAX_OFFSET_M,
  type Position,
} from '@reditelji/shared';
import type { RaceRoute } from '../api/types';

/**
 * Where a post sits along the race course. Marshal posts are identified by
 * their kilometre mark in practice, and the offset doubles as a check on
 * hand-entered coordinates.
 */
export interface PostRouteRef {
  routeId: string;
  routeName: string;
  routeColor: string;
  /** Distance from the course start to the post, in metres. */
  kmMarkM: number;
  /** Perpendicular distance from the post to the course, in metres. */
  offsetM: number;
}

export interface LocatedPost {
  id: string;
  lat: number | null;
  lng: number | null;
}

/**
 * Nearest course to every located post. Derived on demand rather than stored:
 * roughly 60 posts against 4 500 positions is a few milliseconds, and nothing
 * can drift out of step with the geometry after a re-import.
 *
 * Placeholder routes are skipped — a straight sector-to-sector line would
 * quote a kilometre mark that does not exist on the ground.
 */
export function routeRefsByPost(
  posts: LocatedPost[],
  routes: RaceRoute[],
): Map<string, PostRouteRef> {
  const courses = routes
    .filter((r) => r.source === 'gpx')
    .map((r) => ({ row: r, lines: geometryToLines(r.geojson) }));

  const out = new Map<string, PostRouteRef>();
  if (!courses.length) return out;

  for (const post of posts) {
    if (post.lat === null || post.lng === null) continue;
    const point: Position = [post.lng, post.lat];
    let best: PostRouteRef | null = null;
    for (const { row, lines } of courses) {
      const ref = nearestOnRoute(point, lines);
      if (!ref) continue;
      if (!best || ref.offsetM < best.offsetM)
        best = {
          routeId: row.id,
          routeName: row.name,
          routeColor: row.color,
          kmMarkM: ref.distanceAlongM,
          offsetM: ref.offsetM,
        };
    }
    if (best) out.set(post.id, best);
  }
  return out;
}

/** Is this post close enough to the course for its kilometre mark to mean anything? */
export function isOnCourse(ref: PostRouteRef): boolean {
  return ref.offsetM <= ON_COURSE_MAX_OFFSET_M;
}

/** A short distance in the unit that reads naturally: "320 m" / "1,4 km". */
export function formatDistance(metres: number): string {
  return metres < 1000 ? `${Math.round(metres)} m` : formatKm(metres);
}

/** "km 63,4" — the kilometre mark alone. */
export function formatKmMark(metres: number): string {
  return `km ${(metres / 1000).toFixed(1).replace('.', ',')}`;
}

/** "120 km · km 63,4" for a post on the course, or the off-course warning. */
export function describeRouteRef(ref: PostRouteRef): string {
  if (!isOnCourse(ref)) return `ni blizu nobene trase (${formatDistance(ref.offsetM)} stran)`;
  return `${ref.routeName} · ${formatKmMark(ref.kmMarkM)}`;
}
